import * as React from "react";
import Editor from "../editor/Editor";
import { inject } from "mobx-react";
import { observer } from "mobx-react";
import NodeLayer, {
  nodeColors,
  questionTypes,
  narrativeSplitPoint,
} from "../editor/NodeLayer";
import autobind from "autobind-decorator";
import {
  Label,
  MultiLineInput,
  ColorInputRow,
  ColorInput,
  StyledDropdown,
  SingleLineInput,
} from "./EditorViewStyles";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import arrayMove from "array-move";
import { styled } from "@mui/material/styles";
import Toggle from "react-toggle";
import "../libraries/ReactToggle.css";
import HelpIcon from "@mui/icons-material/Help";
import {
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Box,
} from "@mui/material";
import { observable } from "mobx";
import CustomStylesUnput from "./components/CustomStylesUnput";
import { parseStyles } from "../util/util";

const ListInputStyled = styled(SingleLineInput)({
  border: "none",
  marginBottom: 0,
  background: "transparent",
});

const ListItemStyled = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  background: "white",
  padding: "0px 8px",
  marginBottom: "4px",
});

const ErrorPara = styled("p")({
  marginTop: "0px",
  color: "rgb(211, 69, 91)",
  textAlign: "center",
  fontSize: "10px",
});

const DragHandle = SortableHandle(() => (
  <span style={{ cursor: "pointer", userSelect: "none", marginRight: "8px" }}>
    <i className="material-icons" style={{ color: "rgb(120, 120, 120)" }}>
      {" "}
      list{" "}
    </i>
  </span>
));

const ListItem = SortableElement(
  ({ value, onChange, onRemove, onKeyPress }) => (
    <ListItemStyled>
      <DragHandle />
      <ListInputStyled
        autoFocus
        style={{ border: "none" }}
        type="text"
        value={value}
        onChange={onChange}
        placeholder="Enter Choice"
        onKeyDown={onKeyPress}
      />
      <i
        className="material-icons"
        style={{
          color: "rgb(120, 120, 120)",
          fontSize: "14px",
          cursor: "pointer",
        }}
        onClick={onRemove}
      >
        {" "}
        close{" "}
      </i>
    </ListItemStyled>
  )
);

const List = SortableContainer(({ children }) => {
  return <div>{children}</div>;
});

const TemplateInput = styled(MultiLineInput)({
  marginBottom: "8px",
  ".error": {
    borderColor: "rgb(211, 69, 91) !important",
  },
});

const helpText = {
  question:
    "Please enter the question that you would want to appear in the questionnaire Container ",
  startQuestion:
    "Set this to 'ON' if you wanted your questionnaire to start with the selected container ",
  lastQuestion: `When enabled, the Terminal Question provides the final answer without further user input. It concludes the flowchart, offering a seamless user experience. Choose it as the ultimate destination for a concise and conclusive outcome. Remember, no additional paths can be accessed beyond the Terminal Question.\n\n Please note that the option you are trying to access is only available when the "REFERRAL URL" option is disabled`,
  questionType:
    "Here you can set the type of the question for the clients to either choose from available options or give their own answers: \n FREE RESPONSE – Allows respondent to freely respond in their own words. \n YES OR NO – Allows respondent to answer YES or NO only to your question. \n SINGLE CHOICE – Allows respondent to only pick one answer choice from the multiple choices offered to them.\n MULTIPLE CHOICE – Allows respondent to pick multiple choices from the multiple choices offered to them.",
  narrativeTemplate: `Your customer's answer to your question will be outputted as a narrative. For this to happen, type in the narrative you want and include {{{value}}} to display their answer to your question.  For instance, if your question asks your customer "What size pizza do you want?" and your choices are "Small, medium, Large", then you could write below for the narrative: "The customer wants a pizza that is size {{{value}}}`,
  customFinalMessage:
    'A custom message is displayed after all the responses submitted by the client. The default message is "Thank you for your responses. Your responses are submitted." ',
  redirectUrl:
    "Put in a website URL you would like your respondent to get redirected to after they've completed your form, survey, questionnaire, decision tree or workflow.\n Otherwise, if left blank, by default, your respondents are redirected to the FlowCharts.ai website once they've completed your form. ",
  enabledName:
    'When sharing your survey, form, questionnaire, decision tree, workflow or flowchart via text message, email, link, etc., by default, the bot will automatically ask for the name of the person that will be submitting the form. This is so you can see who exactly submitted your form and their responses can be seen in the "View Responses" section. This is turned on by default. \n \n \n However, if you decide to turn this off or disable this feature, then the system will not prompt to ask for the name of the user that is completing your workflow and therefore on the "View Responses" section the user\'s submission will appear as Anonymous Guest. ',
  customStyles: `
    You can customize the button using the following properties:
    - backgroundColor: Specify the background color.
    - color: Specify the text color.
    - hoverBackgroundColor: Specify the background color on hover.
    - hoverColor: Specify the text color on hover.
    
    Example custom styles:
    backgroundColor: red; color: white; hoverBackgroundColor: blue;
  `,
  referralUrl:
    "Enable this option for automatic redirection to a referral link after completing the survey. Simplify your journey to your intended destination after providing valuable feedback.\n Please note that the option you are trying to access is only available when the 'FINAL QUESTION' option is disabled",
};

type NodeLayerPanelViewProps = {
  editor?: Editor;
  className?: string;
  style?: React.CSSProperties;
};

type NodeLayerPanelViewState = {
  isQuestChecked: boolean;
  isNameEnabled: boolean;
};

@inject("editor")
@observer
export default class NodeLayerPanelView extends React.Component<NodeLayerPanelViewProps> {
  @observable
  private _isHelpOpen: boolean = false;

  @observable
  private _isHelpCustomStylesOpen: boolean = false;

  @observable
  private _helpMessage: string = "";

  state: NodeLayerPanelViewState = {
    isQuestChecked:
      this.props.editor.selectedLayer == null ||
      this.props.editor.startLayer?.id != this.props.editor.selectedLayer?.id
        ? false
        : true,
    isNameEnabled: true,
  };

  @autobind
  handleQuestionTextChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    editor.selectedLayer.setQuestionText(ev.target.value);
  }

  @autobind
  handleAddValue(template: string, option?: string) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    if (option) {
      if (option === "YES") {
        template = `${template} {{{value}}}${narrativeSplitPoint}${
          this.getNarrativeTemplate().split(narrativeSplitPoint)[1]
        }`;
      } else if (option === "NO") {
        template = `${
          this.getNarrativeTemplate().split(narrativeSplitPoint)[0]
        }${narrativeSplitPoint}${template} {{{value}}}`;
      }
    } else {
      template = `${template} {{{value}}}`;
    }

    editor.selectedLayer.setNarrativeTemplate(template);
    editor.selectedLayer.compileNarrativeTemplate();
  }

  @autobind
  handleNarrativeTemplateChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    editor.selectedLayer.setNarrativeTemplate(ev.target.value);
    editor.selectedLayer.compileNarrativeTemplate();
  }

  @autobind
  handleNarrativeTemplateChangedForYes(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    let template = `${ev.target.value}${narrativeSplitPoint}${
      this.getNarrativeTemplate().split(narrativeSplitPoint)[1]
    }`;
    editor.selectedLayer.setNarrativeTemplate(template);
    editor.selectedLayer.compileNarrativeTemplate();
  }

  @autobind
  handleNarrativeTemplateChangedForNo(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    let template = `${
      this.getNarrativeTemplate().split(narrativeSplitPoint)[0]
    }${narrativeSplitPoint}${ev.target.value}`;
    editor.selectedLayer.setNarrativeTemplate(template);
    editor.selectedLayer.compileNarrativeTemplate();
  }

  @autobind
  handleQuestionTypeChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    editor.selectedLayer.setQuestionType(ev.value);
  }

  @autobind
  handleSortEnd({ oldIndex, newIndex }) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return false;
    }

    var items = editor.selectedLayer.choices;
    items = arrayMove(items, oldIndex, newIndex);

    editor.selectedLayer.setChoices(items);
  }

  getQuestionText(): string {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }
    return editor.selectedLayer.questionText;
  }

  getNarrativeTemplate(): string {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }

    return editor.selectedLayer.narrativeTemplate;
  }

  getNarrativeCompileError(): string {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }

    return editor.selectedLayer.narrativeCompileError;
  }

  getQuestionType(): string {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }

    if (editor.selectedLayer.questionType === "Single Choice") {
      editor.selectedLayer.setQuestionType("Single Response");
    }
    if (editor.selectedLayer.questionType === "Multiple Choice") {
      editor.selectedLayer.setQuestionType("Multiple Response");
    }

    return editor.selectedLayer.questionType;
  }

  questionHasChoices(): boolean {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return false;
    }

    return editor.selectedLayer.hasChoices;
  }

  isSelectedColor(code: number): boolean {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return false;
    }

    return editor.selectedLayer.colorCode == code;
  }

  setSelectedColor(code: number) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }

    editor.selectedLayer.setColorCode(code);
  }

  @autobind
  handleAddChoice() {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }

    var length = editor.selectedLayer.choices.length + 1;
    editor.selectedLayer.addChoice("");
  }

  isStartQuestion(): boolean {
    var { editor } = this.props;
    return editor.selectedLayer == null ||
      editor.startLayer != editor.selectedLayer
      ? false
      : true;
  }

  @autobind
  setStartQuestion(e) {
    var { editor } = this.props;
    var checked = e.target.checked;

    if (checked) {
      editor.setStartLayer(editor.selectedLayer as NodeLayer);
      this.setState({ isQuestChecked: true });
    } else {
      editor.setStartLayer(null);
      this.setState({ isQuestChecked: false });
    }
  }

  @autobind
  setFinalQuestion(e) {
    var { editor } = this.props;
    var checked = e.target.checked;
    editor.startLayer.setFinalQuestion(checked);
    if (editor.startLayer.referralUrlStatus && checked) {
      editor.startLayer.setEnabledReferralUrl(false);
    }
  }
  // componentDidMount() {
  //   const { startLayer } = this.props.editor;
  //   if (!startLayer.enabledName) {
  //     startLayer.setEnabledName(true);
  //   }
  // }

  @autobind
  setEnabledReferralUrl(e: any) {
    var { editor } = this.props;
    var checked = e.target.checked;
    editor.startLayer.setEnabledReferralUrl(checked);
    if (editor.startLayer.finalQuestionStatus && checked) {
      editor.startLayer.setFinalQuestion(false);
    }
  }

  componentDidUpdate() {
    const { editor } = this.props;
    if (
      this.state.isQuestChecked !=
      (editor.selectedLayer == editor.startLayer)
    ) {
      this.setState({
        isQuestChecked: editor.selectedLayer == editor.startLayer,
      });
    }
  }

  renderChoicesSelector() {
    var { editor } = this.props;

    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }

    var selectedLayer = editor.selectedLayer;

    if (!selectedLayer.hasChoices) {
      return null;
    }

    if (selectedLayer.questionType == questionTypes.yesNo) {
      return null;
    }

    var items = selectedLayer.choices;

    return (
      <React.Fragment>
        <Label>
          <span>CHOICES</span>
          <div style={{ flexGrow: 1 }} />
          <i
            className="material-icons"
            style={{ cursor: "pointer" }}
            onClick={this.handleAddChoice}
          >
            {" "}
            add{" "}
          </i>
        </Label>
        <div style={{ height: "8px" }} />
        <List onSortEnd={this.handleSortEnd} useDragHandle>
          {items.map((value, index) => (
            <ListItem
              key={`item-${index}`}
              index={index}
              value={value}
              onChange={(ev) => selectedLayer.setChoice(index, ev.target.value)}
              onRemove={() => selectedLayer.removeChoice(index)}
              onKeyPress={(event) =>
                event.keyCode === 13 ? this.handleAddChoice() : null
              }
            />
          ))}
        </List>
      </React.Fragment>
    );
  }
  renderChoicesSelectorStyles() {
    var { editor } = this.props;

    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }

    var selectedLayer = editor.selectedLayer;

    if (!selectedLayer.hasChoices) {
      return null;
    }

    if (selectedLayer.questionType == questionTypes.yesNo) {
      return null;
    }

    const items = selectedLayer.choices;
    const choicesStyles = selectedLayer.choicesStyles;

    return (
      <Box>
        {items.map((value, idx) => (
          <CustomStylesUnput
            key={idx}
            placeholder={`Styles for "${
              value.length > 15 ? `${value.slice(15)}...` : value
            }"`}
            value={choicesStyles[idx]}
            onChange={(ev) => {
              selectedLayer.setChoiceStyle(idx, ev.target.value);
            }}
          />
        ))}
      </Box>
    );
  }
  renderYesNoStyles() {
    var { editor } = this.props;

    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }

    var selectedLayer = editor.selectedLayer;

    if (selectedLayer.questionType !== questionTypes.yesNo) {
      return null;
    }

    return (
      <Box>
        <CustomStylesUnput
          placeholder='Styles for "YES"'
          value={selectedLayer.yesChoiceStyle}
          onChange={(ev) => selectedLayer.setYesChoiceStyle(ev.target.value)}
        />
        <CustomStylesUnput
          placeholder='Styles for "NO"'
          value={selectedLayer.noChoiceStyle}
          onChange={(ev) => selectedLayer.setNoChoiceStyle(ev.target.value)}
        />
      </Box>
    );
  }

  getNarrativeSplit(split: number): string {
    var ret = this.getNarrativeTemplate().split(narrativeSplitPoint)[split];

    if (ret == null || ret == "undefined") {
      return "";
    }

    return ret;
  }

  handleClickHelp(message: string) {
    this._helpMessage = message;
    this._isHelpOpen = true;
  }

  @autobind
  handleFinalMessage(): string {
    const { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }

    // return editor.selectedLayer.fina
    return editor.selectedLayer.finalMessage;
  }

  @autobind
  handleFinalMessageChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    editor.selectedLayer.setFinalMessage(ev.target.value);
  }

  @autobind
  handleEnabledName() {
    const { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    return editor.selectedLayer.enabledName;
  }

  @autobind
  handleEnabledNameChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }
    editor.selectedLayer.setEnabledName(ev.target.checked);
  }

  @autobind
  handleRedirectUrl(): string {
    const { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return "";
    }

    return editor.selectedLayer.redirectUrl;
  }

  @autobind
  handleRedirectUrlChanged(ev: any) {
    var { editor } = this.props;
    if (
      editor.selectedLayer == null ||
      !(editor.selectedLayer instanceof NodeLayer)
    ) {
      return;
    }
    editor.selectedLayer.setRedirectUrl(ev.target.value);
  }

  render() {
    const { editor } = this.props;
    const selectedLayer = editor.selectedLayer as NodeLayer;
    var options = [];
    for (var key in questionTypes) {
      options.push(questionTypes[key]);
    }
    var compileError = this.getNarrativeCompileError();

    return (
      <React.Fragment key="node-layer-panel">
        <Box paddingX="16px">
          <Label> COLOR </Label>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "12px",
              marginBottom: "8px",
            }}
          >
            <ColorInputRow>
              {nodeColors.map((color, index) => (
                <ColorInput
                  color={color.bg}
                  key={index}
                  selected={this.isSelectedColor(index)}
                  onClick={() => this.setSelectedColor(index)}
                />
              ))}
            </ColorInputRow>
          </div>
        </Box>
        <Box paddingX="16px">
          <Label>
            {" "}
            QUESTION
            <IconButton onClick={() => this.handleClickHelp(helpText.question)}>
              <HelpIcon fontSize="small" />
            </IconButton>
          </Label>
          <MultiLineInput
            value={this.getQuestionText()}
            onChange={this.handleQuestionTextChanged}
          />
        </Box>
        <Box paddingX="16px">
          <Label>
            <span>
              {" "}
              START QUESTION
              <IconButton
                onClick={() => this.handleClickHelp(helpText.startQuestion)}
              >
                <HelpIcon fontSize="small" />
              </IconButton>
            </span>
            <div style={{ flexGrow: 1 }} />
            <Toggle
              checked={this.state.isQuestChecked}
              onChange={this.setStartQuestion}
            />
          </Label>
        </Box>
        {editor.startLayer?.id === selectedLayer.id && (
          <Box paddingX="16px">
            <Label>
              <span>
                COLLECT NAME?
                <IconButton
                  onClick={() => this.handleClickHelp(helpText.enabledName)}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </span>
              <div style={{ flexGrow: 1 }} />
              <Toggle
                checked={this.handleEnabledName()}
                onChange={this.handleEnabledNameChanged}
              />
            </Label>
          </Box>
        )}
        <Box paddingX="16px">
          {this.state?.isQuestChecked && (
            <Label>
              <span>
                {" "}
                FINAL QUESTION
                <IconButton
                  onClick={() => this.handleClickHelp(helpText.lastQuestion)}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </span>
              <div style={{ flexGrow: 1 }} />
              <Toggle
                checked={editor?.startLayer?.finalQuestionStatus}
                onChange={this.setFinalQuestion}
              />
            </Label>
          )}
        </Box>

        {editor.startLayer?.id === selectedLayer.id && (
          <Box paddingX="16px">
            <Label>
              <span>
                {" "}
                REFERRAL URL
                <IconButton
                  onClick={() => this.handleClickHelp(helpText.referralUrl)}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </span>
              <div style={{ flexGrow: 1 }} />
              <Toggle
                checked={editor?.startLayer?.referralUrlStatus}
                onChange={this.setEnabledReferralUrl}
              />
            </Label>
          </Box>
        )}

        {editor.startLayer?.id === selectedLayer.id &&
          editor.startLayer.referralUrlStatus && (
            <Box
              borderLeft="2px solid #578db8"
              pl="26px"
              pr="16px"
              bgcolor="#cee8f0"
            >
              <Label>
                REDIRECT URL
                <IconButton
                  onClick={() => this.handleClickHelp(helpText.redirectUrl)}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Label>
              <TemplateInput
                value={this.handleRedirectUrl()}
                placeholder="http://www.flowcharts.ai"
                onChange={this.handleRedirectUrlChanged}
                className={compileError != "" ? "error" : ""}
              />
            </Box>
          )}

        <Box paddingX="16px" marginBottom="12px">
          <Label>
            QUESTION TYPE
            <IconButton
              onClick={() => this.handleClickHelp(helpText.questionType)}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Label>
          <StyledDropdown
            options={options}
            value={this.getQuestionType()}
            onChange={this.handleQuestionTypeChanged}
          />
          {this.renderChoicesSelector()}
        </Box>

        <div style={{ height: "12px" }} />
        {this.getQuestionType() !== "Without Response" && (
          <Box paddingX="16px" marginBottom="12px">
            <Label>
              {" "}
              NARRATIVE TEMPLATE
              <IconButton
                onClick={() => this.handleClickHelp(helpText.narrativeTemplate)}
              >
                <HelpIcon fontSize="small" />
              </IconButton>
            </Label>
            {this.getQuestionType() === "Yes/No" ? (
              <div>
                <Label> For 'Yes' </Label>
                <TemplateInput
                  value={this.getNarrativeSplit(0)}
                  onChange={this.handleNarrativeTemplateChangedForYes}
                  className={compileError != "" ? "error" : ""}
                />
                <div style={{ height: "12px" }} />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    this.handleAddValue(this.getNarrativeSplit(0), "YES")
                  }
                >
                  Answer
                </Button>
                <Label> For 'No' </Label>
                <TemplateInput
                  value={this.getNarrativeSplit(1)}
                  onChange={this.handleNarrativeTemplateChangedForNo}
                  className={compileError != "" ? "error" : ""}
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    this.handleAddValue(this.getNarrativeSplit(1), "NO")
                  }
                >
                  Answer
                </Button>
              </div>
            ) : this.getQuestionType() === "Without Response" ? null : (
              <>
                <TemplateInput
                  value={this.getNarrativeTemplate()}
                  onChange={this.handleNarrativeTemplateChanged}
                  className={compileError != "" ? "error" : ""}
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    this.handleAddValue(this.getNarrativeTemplate())
                  }
                >
                  Answer
                </Button>
              </>
            )}
          </Box>
        )}

        {this.getQuestionType() !== "Free Response" &&
          this.getQuestionType() !== "Without Response" && (
            <Box paddingX="16px" marginBottom="12px">
              <Label>
                CUSTOM BUTTON STYLES
                <IconButton
                  onClick={() => (this._isHelpCustomStylesOpen = true)}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Label>
              {this.getQuestionType() === "Yes/No"
                ? this.renderYesNoStyles()
                : this.renderChoicesSelectorStyles()}
            </Box>
          )}

        <Box paddingX="16px" marginBottom="12px">
          <Label>
            {" "}
            FINAL MESSAGE
            <IconButton
              onClick={() => this.handleClickHelp(helpText.customFinalMessage)}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Label>
          <TemplateInput
            value={this.handleFinalMessage()}
            onChange={this.handleFinalMessageChanged}
            placeholder="Thank you for your responses. Your responses are submitted."
          />
        </Box>

        <ErrorPara> {compileError} </ErrorPara>
        <Dialog
          open={this._isHelpOpen}
          onClose={() => (this._isHelpOpen = false)}
          maxWidth="md"
        >
          <DialogContent>
            <span>
              {this._helpMessage.split("\n").map((text, index) => (
                <DialogContentText key={index}>{text}</DialogContentText>
              ))}
            </span>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => (this._isHelpOpen = false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={this._isHelpCustomStylesOpen}
          onClose={() => (this._isHelpCustomStylesOpen = false)}
          maxWidth="md"
        >
          <DialogContent>
            <span>
              <DialogContentText key={0} style={{ marginBottom: "10px" }}>
                {" "}
                You can customize the button using the following properties:
              </DialogContentText>
              <DialogContentText key={1}>
                - backgroundColor: Specify the background color.
              </DialogContentText>
              <DialogContentText key={2}>
                - color: Specify the text color.
              </DialogContentText>
              <DialogContentText key={3}>
                - hoverBackgroundColor: Specify the background color on hover.
              </DialogContentText>
              <DialogContentText key={4}>
                - hoverColor: Specify the text color on hover.
              </DialogContentText>
              <DialogContentText key={5} style={{ marginTop: "10px" }}>
                Example custom styles:
              </DialogContentText>
              <Box
                mt="4px"
                p={4}
                fontStyle="italic"
                display="flex"
                flexDirection="column"
                bgcolor="#f4f4f4"
                borderRadius="12px"
                border="1px solid #ccc"
              >
                <p>backgroundColor : #000;</p>
                <p>color : #928571;</p>
                <p>hoverBackgroundColor : red;</p>
                <p>hoverColor : #fff;</p>
              </Box>
            </span>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => (this._isHelpCustomStylesOpen = false)}
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}
